/**
 * Step5CoInsured Component
 *
 * Collects co-insured driver information via two-part flow:
 * 1. Gate question: "Is anyone else listed as a driver on your policy?" (Yes/No radio)
 * 2. If Yes: "How many?" selector to set driver count
 * 3. Renders repeatable driver blocks using useFieldArray
 *
 * Each driver block collects:
 * - Full legal name (required, no same-as-primary option)
 * - Date of birth (required, no same-as-primary option)
 * - Phone (with SameAsPrimaryToggle)
 * - Mailing address (street, unit, city, state, ZIP with SameAsPrimaryToggle)
 * - License state (with SameAsPrimaryToggle)
 * - Marital status (with SameAsPrimaryToggle)
 * - Job title (required, no same-as-primary option)
 * - Employer (with SameAsPrimaryToggle)
 * - Student status (toggle with conditional school name, program, full-time/part-time)
 * - Relationship to primary (select: 6 options)
 *
 * Includes "Add another driver" and "Remove" buttons for dynamic form management.
 * Uses react-hook-form's useFormContext, Controller, and useFieldArray.
 * Validates against step5Schema from formSchemas.
 */

import { useFormContext, Controller, useFieldArray } from 'react-hook-form';
import { Emery } from '@/components/ui/Calla';
import { EMERY_MESSAGES } from '@/constants/callaMessages';
import { FormField } from '@/components/ui/FormField';
import { AnimatedReveal } from '@/components/ui/AnimatedReveal';
import { DateOfBirthInput } from '@/components/ui/DateOfBirthInput';
import { SameAsPrimaryToggle } from '@/components/ui/SameAsPrimaryToggle';
import { Trash2, Plus } from 'lucide-react';
import { US_STATES } from '@/constants/states';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { type FullFormData, EDUCATION_LEVELS, EDUCATION_LABELS } from '@/schemas/formSchemas';

function getEmptyDriver(): NonNullable<FullFormData['step5']['drivers']>[number] {
  return {
    fullLegalName: '',
    dateOfBirth: '',
    phoneSameAsPrimary: false,
    phone: '',
    addressSameAsPrimary: false,
    mailingAddressStreet: '',
    mailingAddressUnit: '',
    mailingAddressCity: '',
    mailingAddressState: '',
    mailingAddressZip: '',
    licenseStateSameAsPrimary: false,
    licenseState: '',
    maritalStatusSameAsPrimary: false,
    maritalStatus: '',
    jobTitle: '',
    employerSameAsPrimary: false,
    employerName: '',
    isStudent: false,
    studentSchool: '',
    studentProgram: '',
    studentFullTime: false,
    studentDegreePursuing: '',
    yearFirstLicensed: undefined,
    educationLevel: '',
    relationshipToPrimary: 'other',
  };
}

export default function Step5CoInsured() {
  const { control, formState: { errors }, watch } = useFormContext<FullFormData>();
  const hasCoInsured = watch('step5.hasCoInsured');

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'step5.drivers',
  });

  // Handle driver count change
  const handleDriverCountChange = (count: number) => {
    if (count > fields.length) {
      // Add new drivers
      for (let i = fields.length; i < count; i++) {
        append(getEmptyDriver());
      }
    } else if (count < fields.length) {
      // Remove drivers from the end
      for (let i = fields.length - 1; i >= count; i--) {
        remove(i);
      }
    }
  };

  return (
    <div>
      <Emery message={hasCoInsured ? EMERY_MESSAGES.step5_yes : EMERY_MESSAGES.step5_no} />

      {/* Gate Question: Do you have other drivers? */}
      <div className="mb-8">
        <label className="form-label">Is anyone else listed as a driver on your policy?</label>
        <div className="mt-3 flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <Controller
              name="step5.hasCoInsured"
              control={control}
              render={({ field: { value, ...field } }) => (
                <input
                  {...field}
                  type="radio"
                  value="true"
                  checked={value === true}
                  onChange={() => field.onChange(true)}
                  className="w-4 h-4 text-brand-primary focus:ring-brand-primary cursor-pointer"
                />
              )}
            />
            <span className="text-sm font-medium text-gray-700">Yes</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <Controller
              name="step5.hasCoInsured"
              control={control}
              render={({ field: { value, ...field } }) => (
                <input
                  {...field}
                  type="radio"
                  value="false"
                  checked={value === false}
                  onChange={() => field.onChange(false)}
                  className="w-4 h-4 text-brand-primary focus:ring-brand-primary cursor-pointer"
                />
              )}
            />
            <span className="text-sm font-medium text-gray-700">No</span>
          </label>
        </div>
      </div>

      {/* Show driver count selector only if hasCoInsured is true */}
      {hasCoInsured && (
        <div className="mb-8">
          <FormField
            label="How many other drivers?"
            name="step5.coInsuredCount"
            required={true}
            error={errors.step5?.coInsuredCount?.message}
          >
            <Controller
              name="step5.coInsuredCount"
              control={control}
              render={({ field }) => (
                <select
                  {...field}
                  value={field.value || ''}
                  onChange={(e) => {
                    const count = parseInt(e.target.value);
                    field.onChange(count);
                    handleDriverCountChange(count);
                  }}
                  className="form-input"
                >
                  <option value="">Select a number</option>
                  {[1, 2, 3, 4, 5].map((num) => (
                    <option key={num} value={num}>
                      {num} driver{num !== 1 ? 's' : ''}
                    </option>
                  ))}
                </select>
              )}
            />
          </FormField>
        </div>
      )}

      {/* Render driver blocks only if hasCoInsured and drivers exist */}
      {hasCoInsured && fields.length > 0 && (
        <div className="space-y-10">
          {fields.map((field, driverIndex) => (
            <CoInsuredDriverBlock
              key={field.id}
              driverIndex={driverIndex}
              control={control}
              errors={errors}
              onRemove={() => remove(driverIndex)}
              canRemove={fields.length > 1}
            />
          ))}

          {/* Add another driver button (hidden if at 5 drivers max) */}
          {fields.length < 5 && (
            <button
              type="button"
              onClick={() => {
                append(getEmptyDriver());
              }}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-md border border-dashed border-gray-300 bg-gray-50 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
            >
              <Plus size={16} />
              Add Another Driver
            </button>
          )}
        </div>
      )}
    </div>
  );
}

interface CoInsuredDriverBlockProps {
  driverIndex: number;
  control: any;
  errors: any;
  onRemove: () => void;
  canRemove: boolean;
}

function CoInsuredDriverBlock({
  driverIndex,
  control,
  errors,
  onRemove,
  canRemove,
}: CoInsuredDriverBlockProps) {
  const { watch } = useFormContext<FullFormData>();
  const isStudent = watch(`step5.drivers.${driverIndex}.isStudent`);
  const phoneSameAsPrimary = watch(`step5.drivers.${driverIndex}.phoneSameAsPrimary`);
  const addressSameAsPrimary = watch(`step5.drivers.${driverIndex}.addressSameAsPrimary`);
  const licenseStateSameAsPrimary = watch(`step5.drivers.${driverIndex}.licenseStateSameAsPrimary`);
  const maritalStatusSameAsPrimary = watch(`step5.drivers.${driverIndex}.maritalStatusSameAsPrimary`);
  const employerSameAsPrimary = watch(`step5.drivers.${driverIndex}.employerSameAsPrimary`);

  const driverErrors = errors.step5?.drivers?.[driverIndex];

  return (
    <div className="border-l-4 border-brand-primary/30 pl-6 py-4 rounded-lg bg-gray-50">
      <div className="flex justify-between items-start mb-6">
        <h3 className="text-lg font-semibold text-gray-900">
          Driver {driverIndex + 1}
        </h3>
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="text-gray-400 hover:text-red-600 transition-colors"
            title="Remove this driver"
          >
            <Trash2 size={18} />
          </button>
        )}
      </div>

      {/* Full Legal Name - NOT same-as-primary */}
      <FormField
        label="Full Legal Name"
        name={`step5.drivers.${driverIndex}.fullLegalName`}
        required={true}
        error={driverErrors?.fullLegalName?.message}
      >
        <Controller
          name={`step5.drivers.${driverIndex}.fullLegalName`}
          control={control}
          render={({ field }) => (
            <input
              {...field}
              type="text"
              autoComplete="off"
              autoCapitalize="words"
              enterKeyHint="next"
              data-sensitive=""
              placeholder="John Q. Smith"
              className={`form-input ${
                driverErrors?.fullLegalName ? 'form-input-error' : ''
              }`}
            />
          )}
        />
      </FormField>

      {/* Date of Birth - NOT same-as-primary */}
      <FormField
        label="Date of Birth"
        name={`step5.drivers.${driverIndex}.dateOfBirth`}
        required={true}
        error={driverErrors?.dateOfBirth?.message}
      >
        <Controller
          name={`step5.drivers.${driverIndex}.dateOfBirth`}
          control={control}
          render={({ field }) => (
            <DateOfBirthInput value={field.value} onChange={field.onChange} error={!!driverErrors?.dateOfBirth} />
          )}
        />
      </FormField>

      {/* Phone with SameAsPrimaryToggle */}
      <div className="mb-6">
        <FormField
          label="Phone Number"
          name={`step5.drivers.${driverIndex}.phone`}
          required={!phoneSameAsPrimary}
          error={driverErrors?.phone?.message}
        >
          <Controller
            name={`step5.drivers.${driverIndex}.phone`}
            control={control}
            render={({ field }) => (
              <input
                {...field}
                type="tel"
                inputMode="tel"
                autoComplete="off"
                enterKeyHint="next"
                data-sensitive=""
                placeholder="(555) 123-4567"
                disabled={phoneSameAsPrimary}
                className={`form-input ${
                  phoneSameAsPrimary ? 'opacity-50 bg-gray-100' : ''
                } ${driverErrors?.phone ? 'form-input-error' : ''}`}
              />
            )}
          />
        </FormField>
        <div className="mt-2">
          <Controller
            name={`step5.drivers.${driverIndex}.phoneSameAsPrimary`}
            control={control}
            render={({ field }) => (
              <SameAsPrimaryToggle
                label="Same as primary driver"
                checked={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </div>
      </div>

      {/* Address with SameAsPrimaryToggle */}
      <div className="mb-6">
        <FormField
          label="Street Address"
          name={`step5.drivers.${driverIndex}.mailingAddressStreet`}
          required={!addressSameAsPrimary}
          error={driverErrors?.mailingAddressStreet?.message}
        >
          <Controller
            name={`step5.drivers.${driverIndex}.mailingAddressStreet`}
            control={control}
            render={({ field }) => (
              <input
                {...field}
                type="text"
                autoComplete="off"
                autoCapitalize="words"
                enterKeyHint="next"
                data-sensitive=""
                placeholder="123 Main Street"
                disabled={addressSameAsPrimary}
                className={`form-input ${
                  addressSameAsPrimary ? 'opacity-50 bg-gray-100' : ''
                } ${driverErrors?.mailingAddressStreet ? 'form-input-error' : ''}`}
              />
            )}
          />
        </FormField>

        <FormField
          label="Unit / Suite (Optional)"
          name={`step5.drivers.${driverIndex}.mailingAddressUnit`}
          required={false}
          error={driverErrors?.mailingAddressUnit?.message}
        >
          <Controller
            name={`step5.drivers.${driverIndex}.mailingAddressUnit`}
            control={control}
            render={({ field }) => (
              <input
                {...field}
                type="text"
                autoComplete="off"
                enterKeyHint="next"
                placeholder="Unit 5"
                disabled={addressSameAsPrimary}
                className={`form-input ${
                  addressSameAsPrimary ? 'opacity-50 bg-gray-100' : ''
                }`}
              />
            )}
          />
        </FormField>

        <FormField
          label="City"
          name={`step5.drivers.${driverIndex}.mailingAddressCity`}
          required={!addressSameAsPrimary}
          error={driverErrors?.mailingAddressCity?.message}
        >
          <Controller
            name={`step5.drivers.${driverIndex}.mailingAddressCity`}
            control={control}
            render={({ field }) => (
              <input
                {...field}
                type="text"
                autoComplete="off"
                autoCapitalize="words"
                enterKeyHint="next"
                placeholder="Portland"
                disabled={addressSameAsPrimary}
                className={`form-input ${
                  addressSameAsPrimary ? 'opacity-50 bg-gray-100' : ''
                } ${driverErrors?.mailingAddressCity ? 'form-input-error' : ''}`}
              />
            )}
          />
        </FormField>

        <FormField
          label="State"
          name={`step5.drivers.${driverIndex}.mailingAddressState`}
          required={!addressSameAsPrimary}
          error={driverErrors?.mailingAddressState?.message}
        >
          <Controller
            name={`step5.drivers.${driverIndex}.mailingAddressState`}
            control={control}
            render={({ field }) => (
              <SearchableSelect
                options={US_STATES.map((s) => ({ value: s.code, label: s.name }))}
                value={field.value}
                onChange={field.onChange}
                placeholder="Search or select state"
                disabled={addressSameAsPrimary}
                error={!!driverErrors?.mailingAddressState}
              />
            )}
          />
        </FormField>

        <FormField
          label="ZIP Code"
          name={`step5.drivers.${driverIndex}.mailingAddressZip`}
          required={!addressSameAsPrimary}
          error={driverErrors?.mailingAddressZip?.message}
        >
          <Controller
            name={`step5.drivers.${driverIndex}.mailingAddressZip`}
            control={control}
            render={({ field }) => (
              <input
                {...field}
                type="text"
                inputMode="numeric"
                pattern="[0-9]{5}"
                maxLength={5}
                autoComplete="off"
                enterKeyHint="next"
                placeholder="12345"
                disabled={addressSameAsPrimary}
                className={`form-input ${
                  addressSameAsPrimary ? 'opacity-50 bg-gray-100' : ''
                } ${driverErrors?.mailingAddressZip ? 'form-input-error' : ''}`}
              />
            )}
          />
        </FormField>

        <div className="mt-3">
          <Controller
            name={`step5.drivers.${driverIndex}.addressSameAsPrimary`}
            control={control}
            render={({ field }) => (
              <SameAsPrimaryToggle
                label="Same as primary driver"
                checked={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </div>
      </div>

      {/* License State with SameAsPrimaryToggle */}
      <div className="mb-6">
        <FormField
          label="Driver's License State"
          name={`step5.drivers.${driverIndex}.licenseState`}
          required={!licenseStateSameAsPrimary}
          error={driverErrors?.licenseState?.message}
        >
          <Controller
            name={`step5.drivers.${driverIndex}.licenseState`}
            control={control}
            render={({ field }) => (
              <SearchableSelect
                options={US_STATES.map((s) => ({ value: s.code, label: s.name }))}
                value={field.value}
                onChange={field.onChange}
                placeholder="Search or select state"
                disabled={licenseStateSameAsPrimary}
                error={!!driverErrors?.licenseState}
              />
            )}
          />
        </FormField>
        <div className="mt-2">
          <Controller
            name={`step5.drivers.${driverIndex}.licenseStateSameAsPrimary`}
            control={control}
            render={({ field }) => (
              <SameAsPrimaryToggle
                label="Same as primary driver"
                checked={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </div>
      </div>

      {/* Year First Licensed */}
      <FormField
        label="Year first licensed (optional)"
        name={`step5.drivers.${driverIndex}.yearFirstLicensed`}
        required={false}
        error={driverErrors?.yearFirstLicensed?.message}
      >
        <Controller
          name={`step5.drivers.${driverIndex}.yearFirstLicensed`}
          control={control}
          render={({ field }) => (
            <input
              {...field}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={4}
              autoComplete="off"
              enterKeyHint="next"
              placeholder="2015"
              className="form-input"
              onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : '')}
            />
          )}
        />
      </FormField>

      {/* Marital Status with SameAsPrimaryToggle */}
      <div className="mb-6">
        <FormField
          label="Marital Status"
          name={`step5.drivers.${driverIndex}.maritalStatus`}
          required={!maritalStatusSameAsPrimary}
          error={driverErrors?.maritalStatus?.message}
        >
          <Controller
            name={`step5.drivers.${driverIndex}.maritalStatus`}
            control={control}
            render={({ field }) => (
              <select
                {...field}
                disabled={maritalStatusSameAsPrimary}
                className={`form-input ${
                  maritalStatusSameAsPrimary ? 'opacity-50 bg-gray-100' : ''
                } ${driverErrors?.maritalStatus ? 'form-input-error' : ''}`}
              >
                <option value="">Select status</option>
                <option value="divorced">Divorced</option>
                <option value="domestic_partner">Domestic Partner</option>
                <option value="married">Married</option>
                <option value="single">Single</option>
                <option value="widowed">Widowed</option>
              </select>
            )}
          />
        </FormField>
        <div className="mt-2">
          <Controller
            name={`step5.drivers.${driverIndex}.maritalStatusSameAsPrimary`}
            control={control}
            render={({ field }) => (
              <SameAsPrimaryToggle
                label="Same as primary driver"
                checked={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </div>
      </div>

      {/* Education Level */}
      <FormField
        label="Highest education (optional)"
        name={`step5.drivers.${driverIndex}.educationLevel`}
        required={false}
        error={driverErrors?.educationLevel?.message}
      >
        <Controller
          name={`step5.drivers.${driverIndex}.educationLevel`}
          control={control}
          render={({ field }) => (
            <select {...field} className="form-input">
              <option value="">Select education level</option>
              {EDUCATION_LEVELS.map((level) => (
                <option key={level} value={level}>
                  {EDUCATION_LABELS[level]}
                </option>
              ))}
            </select>
          )}
        />
      </FormField>

      {/* Job Title - NO same-as-primary option */}
      <FormField
        label="Job Title"
        name={`step5.drivers.${driverIndex}.jobTitle`}
        required={true}
        error={driverErrors?.jobTitle?.message}
      >
        <Controller
          name={`step5.drivers.${driverIndex}.jobTitle`}
          control={control}
          render={({ field }) => (
            <input
              {...field}
              type="text"
              autoComplete="organization-title"
              autoCapitalize="words"
              enterKeyHint="next"
              placeholder="e.g., Teacher"
              className={`form-input ${
                driverErrors?.jobTitle ? 'form-input-error' : ''
              }`}
            />
          )}
        />
      </FormField>

      {/* Employer with SameAsPrimaryToggle */}
      <div className="mb-6">
        <FormField
          label="Employer Name"
          name={`step5.drivers.${driverIndex}.employerName`}
          required={!employerSameAsPrimary}
          error={driverErrors?.employerName?.message}
        >
          <Controller
            name={`step5.drivers.${driverIndex}.employerName`}
            control={control}
            render={({ field }) => (
              <input
                {...field}
                type="text"
                autoComplete="organization"
                autoCapitalize="words"
                enterKeyHint="next"
                placeholder="Google"
                disabled={employerSameAsPrimary}
                className={`form-input ${
                  employerSameAsPrimary ? 'opacity-50 bg-gray-100' : ''
                } ${driverErrors?.employerName ? 'form-input-error' : ''}`}
              />
            )}
          />
        </FormField>
        <div className="mt-2">
          <Controller
            name={`step5.drivers.${driverIndex}.employerSameAsPrimary`}
            control={control}
            render={({ field }) => (
              <SameAsPrimaryToggle
                label="Same as primary driver"
                checked={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </div>
      </div>

      {/* Student Status */}
      <div className="mb-6">
        <label className="flex items-center gap-3 cursor-pointer">
          <Controller
            name={`step5.drivers.${driverIndex}.isStudent`}
            control={control}
            render={({ field }) => (
              <input
                {...field}
                type="checkbox"
                checked={field.value}
                onChange={(e) => field.onChange(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-brand-primary focus:ring-brand-primary cursor-pointer"
              />
            )}
          />
          <span className="text-sm font-medium text-gray-700">
            This driver is a student
          </span>
        </label>
      </div>

      {/* Conditional: Student Fields */}
      <AnimatedReveal isVisible={isStudent}>
        <div className="space-y-4 mb-6">
          <FormField
            label="School Name"
            name={`step5.drivers.${driverIndex}.studentSchool`}
            required={true}
            error={driverErrors?.studentSchool?.message}
          >
            <Controller
              name={`step5.drivers.${driverIndex}.studentSchool`}
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  type="text"
                  placeholder="State University"
                  className={`form-input ${
                    driverErrors?.studentSchool ? 'form-input-error' : ''
                  }`}
                />
              )}
            />
          </FormField>

          <FormField
            label="Degree pursuing (optional)"
            name={`step5.drivers.${driverIndex}.studentDegreePursuing`}
            required={false}
            error={driverErrors?.studentDegreePursuing?.message}
          >
            <Controller
              name={`step5.drivers.${driverIndex}.studentDegreePursuing`}
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  type="text"
                  placeholder="e.g., Bachelor of Science"
                  className="form-input"
                />
              )}
            />
          </FormField>

          <FormField
            label="Program / Major (Optional)"
            name={`step5.drivers.${driverIndex}.studentProgram`}
            required={false}
            error={driverErrors?.studentProgram?.message}
          >
            <Controller
              name={`step5.drivers.${driverIndex}.studentProgram`}
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  type="text"
                  placeholder="Computer Science"
                  className="form-input"
                />
              )}
            />
          </FormField>

          <div>
            <label className="form-label">Enrollment Status</label>
            <div className="mt-2 flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <Controller
                  name={`step5.drivers.${driverIndex}.studentFullTime`}
                  control={control}
                  render={({ field: { value, ...field } }) => (
                    <input
                      {...field}
                      type="radio"
                      value="true"
                      checked={value === true}
                      onChange={() => field.onChange(true)}
                      className="w-4 h-4 text-brand-primary focus:ring-brand-primary cursor-pointer"
                    />
                  )}
                />
                <span className="text-sm font-medium text-gray-700">Full-time</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Controller
                  name={`step5.drivers.${driverIndex}.studentFullTime`}
                  control={control}
                  render={({ field: { value, ...field } }) => (
                    <input
                      {...field}
                      type="radio"
                      value="false"
                      checked={value === false}
                      onChange={() => field.onChange(false)}
                      className="w-4 h-4 text-brand-primary focus:ring-brand-primary cursor-pointer"
                    />
                  )}
                />
                <span className="text-sm font-medium text-gray-700">Part-time</span>
              </label>
            </div>
          </div>
        </div>
      </AnimatedReveal>

      {/* Relationship to Primary */}
      <FormField
        label="Relationship to Primary Policyholder"
        name={`step5.drivers.${driverIndex}.relationshipToPrimary`}
        required={true}
        error={driverErrors?.relationshipToPrimary?.message}
      >
        <Controller
          name={`step5.drivers.${driverIndex}.relationshipToPrimary`}
          control={control}
          render={({ field }) => (
            <select
              {...field}
              className={`form-input ${
                driverErrors?.relationshipToPrimary ? 'form-input-error' : ''
              }`}
            >
              <option value="">Select relationship</option>
              <option value="child">Child</option>
              <option value="domestic_partner">Domestic Partner</option>
              <option value="parent">Parent</option>
              <option value="sibling">Sibling</option>
              <option value="spouse">Spouse</option>
              <option value="other">Other</option>
            </select>
          )}
        />
      </FormField>
    </div>
  );
}
